import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ToastService } from '../services/toast.service';
import { ApiUrls } from '../constants/api-urls';

let isRefreshing = false;

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Clone request to include credentials
  const authReq = req.clone({
    withCredentials: true
  });

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors
      // Skip token refresh for:
      // - Login endpoints (user is trying to log in)
      // - /me endpoint (initial auth check - let guard handle navigation)
      const isLoginRequest = req.url.includes('/auth/login');
      const isAuthCheckRequest = req.url.includes('/auth/me');

      if (error.status === 401 && !isLoginRequest && !isAuthCheckRequest) {
        // This is a 401 on an API call (not initial auth check)
        // User had a session that expired

        // Prevent multiple simultaneous refresh attempts
        if (isRefreshing) {
          return throwError(() => error);
        }

        isRefreshing = true;

        // Attempt to refresh the token
        return authService.refreshToken().pipe(
          switchMap(() => {
            isRefreshing = false;

            // Retry the original request with new token
            const retryReq = req.clone({
              withCredentials: true
            });
            return next(retryReq);
          }),
          catchError((refreshError) => {
            isRefreshing = false;

            // Refresh failed - user session truly expired
            authService.clearAuthState();

            // Only show toast if user actually had a session
            // (currentUser would have been set during initial login)
            if (authService.currentUser() !== null || sessionStorage.getItem('current_user')) {
              const toastService = inject(ToastService);
              toastService.error('Your session has expired. Please login again.', 'Session Expired');
            }

            router.navigate(['/login']);

            return throwError(() => refreshError);
          })
        );
      }

      return throwError(() => error);
    })
  );
};
